import { useMemo, useState } from 'react';
import { Kanban, Plus, MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useTasksQuery } from '@/hooks/useTasks';
import { useTaskPanelStore } from '@/stores/taskPanelStore';
import { cn } from '@/lib/utils';

interface CardViewProps {
  sheetId: string;
  groupByColumn?: string;
  className?: string;
}

interface TaskCard {
  id: string;
  name: string;
  status: string | null;
  priority: string | number;
  percentComplete: number;
  startDate: string | null;
  finishDate: string | null;
}

// Default status columns for card view
const DEFAULT_COLUMNS = [
  { id: 'not_started', name: 'Not Started', color: 'bg-gray-100' },
  { id: 'in_progress', name: 'In Progress', color: 'bg-blue-100' },
  { id: 'completed', name: 'Completed', color: 'bg-green-100' },
  { id: 'on_hold', name: 'On Hold', color: 'bg-amber-100' },
  { id: 'cancelled', name: 'Cancelled', color: 'bg-red-100' },
];

export function CardView({ sheetId, groupByColumn: _groupByColumn, className }: CardViewProps) {
  const { data: tasks = [], isLoading } = useTasksQuery(sheetId);
  const { openPanel } = useTaskPanelStore();
  const [draggedCard, setDraggedCard] = useState<string | null>(null);

  // Group tasks by status (or custom column)
  const groupedTasks = useMemo(() => {
    const groups: Record<string, TaskCard[]> = {};

    // Initialize all columns
    DEFAULT_COLUMNS.forEach((col) => {
      groups[col.id] = [];
    });

    // Group tasks
    tasks.forEach((task) => {
      // Read status from built-in field or customFields
      const customFields = (task as any).customFields as Record<string, unknown> | undefined;
      let status = task.status as string | null;
      if (!status && customFields) {
        // Find status value from customFields (look for any value matching our status options)
        const cfValues = Object.values(customFields);
        const statusMatch = cfValues.find((v) =>
          typeof v === 'string' && ['not_started', 'in_progress', 'completed', 'on_hold', 'cancelled'].includes(v)
        );
        if (statusMatch) status = statusMatch as string;
      }
      const groupKey = status || 'not_started';
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push({
        id: task.id,
        name: task.name,
        status: task.status,
        priority: task.priority,
        percentComplete: task.percentComplete,
        startDate: task.startDate,
        finishDate: task.finishDate,
      });
    });

    return groups;
  }, [tasks]);

  const handleCardClick = (taskId: string) => {
    openPanel(taskId);
  };

  const handleDragStart = (e: React.DragEvent, taskId: string) => {
    setDraggedCard(taskId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, _columnId: string) => {
    e.preventDefault();
    // TODO: Update task status via API
    setDraggedCard(null);
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex items-center gap-2 text-muted-foreground">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <span>Loading cards...</span>
        </div>
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center text-muted-foreground">
        <Kanban className="mb-4 h-12 w-12" />
        <p className="text-lg font-medium">No tasks yet</p>
        <p className="text-sm">Create tasks to see them in card view</p>
      </div>
    );
  }

  return (
    <div className={cn('flex h-full gap-4 overflow-x-auto p-4', className)}>
      {DEFAULT_COLUMNS.map((column) => (
        <div
          key={column.id}
          className="flex w-72 shrink-0 flex-col rounded-lg bg-muted/50"
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, column.id)}
        >
          {/* Column Header */}
          <div className="flex items-center justify-between p-3">
            <div className="flex items-center gap-2">
              <span className="font-medium">{column.name}</span>
              <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                {groupedTasks[column.id]?.length || 0}
              </Badge>
            </div>
            <Button variant="ghost" size="icon" className="h-6 w-6">
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          {/* Column Cards */}
          <ScrollArea className="flex-1 px-2 pb-2">
            <div className="space-y-2">
              {groupedTasks[column.id]?.map((task) => (
                <Card
                  key={task.id}
                  className={cn(
                    'cursor-pointer transition-shadow hover:shadow-md',
                    draggedCard === task.id && 'opacity-50'
                  )}
                  draggable
                  onDragStart={(e) => handleDragStart(e, task.id)}
                  onClick={() => handleCardClick(task.id)}
                >
                  <CardHeader className="p-3 pb-2">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-sm font-medium leading-tight">
                        {task.name}
                      </CardTitle>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreHorizontal className="h-3 w-3" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-3 pt-0">
                    {/* Progress bar */}
                    <div className="mb-2 h-1.5 w-full rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-primary"
                        style={{ width: `${task.percentComplete}%` }}
                      />
                    </div>

                    {/* Task metadata */}
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{task.percentComplete}% complete</span>
                      {task.finishDate && (
                        <span>
                          Due{' '}
                          {new Date(task.finishDate).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </ScrollArea>
        </div>
      ))}
    </div>
  );
}

export default CardView;
